import { createPageContainer } from "../../components/ui/page-container";
import { attachOrganizerAccountMenuIfAuthenticated } from "../../components/ui/organizer-account-menu";
import { createStandardSiteAppHeader } from "../../components/ui/site-app-header";
import { createSkipLink } from "../../components/ui/skip-link";
import { fetchHealth } from "../../lib/api-health";
import type { Translate } from "../../lib/i18n";
import type { SupportedLocale } from "../../locales/catalog-registry";

export interface RenderHealthOptions {
  /** Cancels in-flight fetch when navigation tears the view down. */
  signal: AbortSignal;
  /** Active locale for the header locale switcher. */
  locale: SupportedLocale;
}

/**
 * Renders the health diagnostic route and loads backend-api health JSON.
 *
 * @param mount - Container cleared and filled by this view.
 * @param t - Localized string resolver.
 * @param options - Lifecycle controls (abort signal and locale).
 */
export function renderHealth(
  mount: HTMLElement,
  t: Translate,
  options: RenderHealthOptions,
): void {
  mount.replaceChildren();

  const shell = document.createElement("div");
  shell.className = "home-shell";

  shell.appendChild(createSkipLink(t("quiz.home.skip_to_main"), "health-main"));

  const page = createPageContainer();
  shell.appendChild(page);

  const { header, authSlot } = createStandardSiteAppHeader(t, options.locale);
  page.appendChild(header);

  void attachOrganizerAccountMenuIfAuthenticated(authSlot, t, {
    signal: options.signal,
  });

  const main = document.createElement("main");
  main.id = "health-main";
  main.className = "health-page";
  main.tabIndex = -1;

  const heading = document.createElement("h1");
  heading.className = "qz-heading qz-heading--1 health-page__title";
  heading.textContent = t("quiz.health.heading");
  main.appendChild(heading);

  const statusEl = document.createElement("p");
  statusEl.className = "health-page__status";
  statusEl.textContent = t("quiz.health.loading");
  main.appendChild(statusEl);

  const bodySection = document.createElement("section");
  bodySection.className = "health-page__body";
  const bodyTitle = document.createElement("h2");
  bodyTitle.className = "qz-heading qz-heading--2 health-page__body-title";
  bodyTitle.textContent = t("quiz.health.responseBody");
  bodySection.appendChild(bodyTitle);
  const pre = document.createElement("pre");
  pre.className = "health-page__pre";
  pre.setAttribute("aria-live", "polite");
  bodySection.appendChild(pre);
  main.appendChild(bodySection);
  bodySection.hidden = true;

  page.appendChild(main);
  mount.appendChild(shell);

  fetchHealth({ signal: options.signal })
    .then(async (res) => {
      statusEl.textContent = t("quiz.health.httpStatus", {
        status: String(res.status),
      });
      const text = await res.text();
      let display = text;
      try {
        display = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        display = text
          ? `${text}\n(${t("quiz.health.notJson")})`
          : t("quiz.health.notJson");
      }
      pre.textContent = display;
      bodySection.hidden = false;
    })
    .catch((err: unknown) => {
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      statusEl.textContent = t("quiz.health.networkError");
      pre.textContent =
        err instanceof Error ? err.message : t("quiz.health.networkError");
      bodySection.hidden = false;
    });
}
